import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  Menu,
  MenuItem,
  Pagination,
  Select,
  Button,
  IconButton,
  Skeleton
} from "@mui/material";
import Swal from "sweetalert2";
import InboxIcon from "@mui/icons-material/Inbox";
import PowerSettingsNewOutlinedIcon from "@mui/icons-material/PowerSettingsNewOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CopyAllOutlinedIcon from "@mui/icons-material/CopyAllOutlined";

import {
  MyAddRoundedIcon,
  MyDeleteOutlineOutlinedIcon,
  MyEditOutlinedIcon,
  MyUnblockIcon,
  MyVisibilityOutlinedIcon,
  MyCheckboxIcon,
  MyBlockOutlinedIcon,
  MyCheckCircleOutlineOutlinedIcon,
  MySearchIcon
} from "../../Assets/svg/MyIcons";
import { TableFilte } from "../../Assets/svg/svgicon";
import { CommonTooltip } from "../../utils/tooltip";
import { ITEMS_PER_PAGE_OPTIONS } from "../../config";
import FilterDrawer from "./FilterDrawer";
import "./DataTable.css";

// Helper function for debouncing
function debounce(func, wait) {
  let timeout;
  const debounced = function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
  debounced.cancel = () => {
    clearTimeout(timeout);
  };
  return debounced;
}

// ─── Skeleton Row ─────────────────────────────────────────────────────────────
const SkeletonRow = ({ colCount, hasBulkActions, hasActions }) => (
  <tr className="dt-skeleton-row">
    {hasBulkActions && (
      <td className="dt-cell dt-cell-checkbox">
        <Skeleton variant="rectangular" width={18} height={18} sx={{ borderRadius: "4px" }} />
      </td>
    )}
    {Array.from({ length: colCount }).map((_, i) => (
      <td key={i} className="dt-cell">
        <Skeleton variant="text" width={`${55 + Math.random() * 35}%`} height={20} />
      </td>
    ))}
    {hasActions && (
      <td className="dt-cell dt-cell-actions">
        <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton variant="circular" width={28} height={28} />
        </div>
      </td>
    )}
  </tr>
);

// ─── Toolbar ──────────────────────────────────────────────────────────────────
const DataTableToolbar = ({
  toolbarActions,
  filters = [],
  filterValues = {},
  addButtonText = "ADD",
  onAddClick = null,
  hasCreatePermission = false,
  selectedRows = [],
  bulkActions = {},
  handleBulkActionConfirm = () => { },
  hasAppliedFilters = false,
  setDrawerOpen = () => { },
  localSearch = "",
  handleSearchChange = () => { },
  handleSearchKeyDown = () => { },
  searchPlaceholder = "Search...",
}) => {
  const hasSearch = filters.some((f) => f.type === "search");
  const drawerFilters = filters.filter((f) => f.type !== "search");

  return (
    <div className="dt-toolbar">
      {/* Left Side */}
      <div className="dt-toolbar-left">
        {/* Bulk Delete */}
        <div className={`dt-delete-wrap${selectedRows.length > 0 && bulkActions.delete ? " dt-delete-wrap-visible" : ""}`}>
          {selectedRows.length > 0 && bulkActions.delete && (
            <CommonTooltip title="Delete Selected" placement="top" arrow>
              <IconButton
                className="dt-bulk-delete-btn"
                onClick={() => handleBulkActionConfirm("delete")}
              >
                <MyDeleteOutlineOutlinedIcon />
              </IconButton>
            </CommonTooltip>
          )}
        </div>

        {hasSearch && (
          <div className="dt-search-wrap">
            <MySearchIcon className="dt-search-icon" />
            <input
              type="text"
              className="dt-search-input"
              placeholder={searchPlaceholder}
              value={localSearch}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
            />
          </div>
        )}
      </div>

      {/* Right Side */}
      <div className="dt-toolbar-right">

        {drawerFilters.length > 0 && (
          <Button
            variant="outlined"
            onClick={(e) => setDrawerOpen(e)}
            startIcon={<TableFilte />}
            className={`dt-filter-btn ${hasAppliedFilters ? "active" : ""}`}
          >
            Filters
          </Button>
        )}


        {hasCreatePermission && onAddClick && (
          <Button
            className="btn btn-primary btn-add"
            onClick={onAddClick}
            startIcon={<MyAddRoundedIcon className="text-white" />}
            sx={{
              textTransform: "none",
              height: "40px",
              borderRadius: "8px",
              fontWeight: 500,
              fontSize: "13px",
              px: 2.5
            }}
          >
            {addButtonText}
          </Button>
        )}
        {toolbarActions}
      </div>
    </div>
  );
};

// ─── Main DataTable ───────────────────────────────────────────────────────────
const DataTable = ({
  columns = [],
  rows = [],
  loading = false,
  totalCount = 0,
  page = 1,
  pageSize = ITEMS_PER_PAGE_OPTIONS[0],
  filters = [],
  filterValues = {},
  actions = {},
  bulkActions = {},
  statusField = "isActive",
  onFilterApply = () => { },
  onFilterReset = () => { },
  onPageChange = () => { },
  onPageSizeChange = () => { },
  onAction = () => { },
  onBulkAction = () => { },
  addButtonText = "ADD",
  onAddClick = null,
  hasCreatePermission = false,
  getRowHeight = null,
  getRowId = null,
  toolbarActions = null,
  sx = {},
  toolbarOnly = false,
  header = null,
  showPagination = true,
  showRowsPerPage = true,
  showCloneTenant = false,
  onCloneTenantAdd = () => { },
  onCloneTenantEdit = () => { },

}) => {
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);

  // Local state for toolbar search input
  const [localSearch, setLocalSearch] = useState(filterValues.search || "");

  // Update localSearch when parent filter values change (e.g., reset)
  useEffect(() => {
    setLocalSearch(filterValues.search || "");
  }, [filterValues.search]);

  // Handle select/deselect all rows
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = rows.map((row) => (getRowId ? getRowId(row) : row.id));
      setSelectedRows(allIds);
    } else {
      setSelectedRows([]);
    }
  };

  // Handle individual row selection
  const handleRowSelect = (event, row) => {
    const rowId = getRowId ? getRowId(row) : row.id;
    if (event.target.checked) {
      setSelectedRows((prev) => [...prev, rowId]);
    } else {
      setSelectedRows((prev) => prev.filter((id) => id !== rowId));
    }
  };

  // Reset local selection when rows change
  useEffect(() => {
    setSelectedRows([]);
  }, [rows]);

  // Bulk Actions Confirmation
  const handleBulkActionConfirm = async (actionType) => {
    let title = "";
    let text = "";
    let confirmButtonText = "";
    let confirmButtonColor = "#22419e";

    if (actionType === "delete") {
      title = `Are you sure you want to delete ${selectedRows.length} selected records?`;
      text = "This action cannot be undone.";
      confirmButtonText = "Yes, Delete";
      confirmButtonColor = "#f1416c";
    } else {
      onBulkAction(actionType, selectedRows);
      return;
    }

    const result = await Swal.fire({
      title,
      text,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor,
      cancelButtonColor: "#777777",
      confirmButtonText,
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      onBulkAction(actionType, selectedRows);
    }
  };

  // Check if we have bulk actions or row actions
  const hasBulkActions = Object.values(bulkActions).some(Boolean);
  const hasActions = Object.values(actions).some(Boolean);

  // Is any filter value dirty/applied? (excluding search)
  const hasAppliedFilters = React.useMemo(() => {
    return Object.entries(filterValues).some(([key, val]) => {
      if (key === "search") return false;
      if (Array.isArray(val)) return val.length > 0;
      if (val && typeof val === "object") return val.from || val.to;
      return val !== undefined && val !== null && val !== "" && val !== "all";
    });
  }, [filterValues]);

  // Filter drawer filters (exclude search)
  const drawerFilters = React.useMemo(() => {
    return filters.filter((f) => f.type !== "search");
  }, [filters]);

  const searchFilter = React.useMemo(() => {
    return filters.find((f) => f.type === "search");
  }, [filters]);

  // Debounced search trigger
  const debouncedApplySearch = useRef(
    debounce((value) => {
      onFilterApply({ ...filterValues, search: value });
    }, 400)
  ).current;

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearch(value);
    debouncedApplySearch(value);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      debouncedApplySearch.cancel();
      onFilterApply({ ...filterValues, search: localSearch });
    }
  };

  // Render a single cell value
  const renderCellContent = (column, row) => {
    if (column.renderCell) {
      return column.renderCell({ row });
    }
    const value = row[column.field];
    return value !== undefined && value !== null ? value : "";
  };

  // Build action cell for a row
  const renderActionCell = (row) => {
    const isActive = row[statusField];
    return (
      <div className="dt-action-cell">
        {actions.view && (
          <CommonTooltip title="View" placement="top" arrow>
            <IconButton
              className="dt-action-btn dt-action-view"
              onClick={() => onAction("view", row)}
            >
              <MyVisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </CommonTooltip>
        )}

        {actions.unblock && row.isBlocked && (
          <CommonTooltip title="Unblock" placement="top" arrow>
            <IconButton
              className="dt-action-btn dt-action-view"
              onClick={() => onAction("unblock", row)}
            >
              <MyUnblockIcon fontSize="small" />
            </IconButton>
          </CommonTooltip>
        )}

        {(actions.activate || actions.deactivate) && (
          <CommonTooltip
            title={isActive ? "Deactivate User" : "Activate User"}
            placement="top"
            arrow
          >
            <IconButton
              className="dt-action-btn"
              onClick={() => onAction(isActive ? "deactivate" : "activate", row)}
            >
              <PowerSettingsNewOutlinedIcon
                fontSize="small"
                color={isActive ? "error" : "success"}
              />
            </IconButton>
          </CommonTooltip>
        )}

        {actions.clone && (
          <CommonTooltip title="Clone Tenant" placement="top" arrow>
            <IconButton
              className="dt-action-btn dt-action-clone"
              onClick={() => onAction("clone", row)}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </CommonTooltip>
        )}

        {actions.edit && (
          <CommonTooltip title="Edit" placement="top" arrow>
            <IconButton
              className="dt-action-btn dt-action-edit"
              onClick={() => onAction("edit", row)}
            >
              <MyEditOutlinedIcon />
            </IconButton>
          </CommonTooltip>
        )}

        {actions.delete && (
          <CommonTooltip title="Delete" placement="top" arrow>
            <IconButton
              className="dt-action-btn dt-action-delete"
              onClick={() => onAction("delete", row)}
            >
              <MyDeleteOutlineOutlinedIcon />
            </IconButton>
          </CommonTooltip>
        )}

        {actions.approve && row.status === "PENDING" && (
          <CommonTooltip title="Approve" placement="top" arrow>
            <IconButton
              className="dt-action-btn dt-action-approve"
              onClick={() => onAction("approve", row)}
            >
              <MyCheckCircleOutlineOutlinedIcon fontSize="small" />
            </IconButton>
          </CommonTooltip>
        )}

        {actions.reject && row.status === "PENDING" && (
          <CommonTooltip title="Reject" placement="top" arrow>
            <IconButton
              className="dt-action-btn dt-action-reject"
              onClick={() => onAction("reject", row)}
            >
              <MyBlockOutlinedIcon fontSize="small" />
            </IconButton>
          </CommonTooltip>
        )}
      </div>
    );
  };

  const totalColSpan = columns.length + (hasBulkActions ? 1 : 0) + (hasActions ? 1 : 0);
  const skeletonRowCount = pageSize > 5 ? 6 : pageSize;

  return (
    <div className="dt-root">
      {/* OPTIONAL HEADER */}
      {header && (
        <div className="dt-header">
          {header}
        </div>
      )}
      {/* ── Toolbar ── */}
      <DataTableToolbar
        filters={filters}
        filterValues={filterValues}
        onFilterReset={() => {
          setLocalSearch("");
          onFilterReset();
        }}
        addButtonText={addButtonText}
        onAddClick={onAddClick}
        hasCreatePermission={hasCreatePermission}
        selectedRows={selectedRows}
        bulkActions={bulkActions}
        handleBulkActionConfirm={handleBulkActionConfirm}
        hasAppliedFilters={hasAppliedFilters}
        setDrawerOpen={(event) => setFilterAnchorEl(event.currentTarget)}
        localSearch={localSearch}
        handleSearchChange={handleSearchChange}
        handleSearchKeyDown={handleSearchKeyDown}
        searchPlaceholder={searchFilter?.placeholder || "Search..."}
        toolbarActions={toolbarActions}
        showCloneTenant={showCloneTenant}
        onCloneTenantAdd={onCloneTenantAdd}
        onCloneTenantEdit={onCloneTenantEdit}
      />

      {/* ── Table ── */}
      {!toolbarOnly && (
        <>
          <div className="dt-table-wrap">
            <table className="dt-table">
              <thead className="dt-thead">
                <tr>
                  {hasBulkActions && (
                    <th className="dt-th dt-th-checkbox">
                      <Checkbox
                        size="small"
                        onChange={handleSelectAll}
                        disabled={rows.length === 0}
                        checked={rows.length > 0 && selectedRows.length === rows.length}
                        indeterminate={selectedRows.length > 0 && selectedRows.length < rows.length}
                        sx={MyCheckboxIcon}
                      />
                    </th>
                  )}
                  {columns.map((col) => (
                    <th
                      key={col.field}
                      className="dt-th"
                      style={{
                        minWidth: col.minWidth ? `${col.minWidth}px` : undefined,
                        width: col.width ? `${col.width}px` : undefined,
                        textAlign: col.headerAlign || "left"
                      }}
                    >
                      {col.headerName || col.field}
                    </th>
                  ))}
                  {hasActions && (
                    <th
                      className="dt-th dt-th-actions"
                      style={{ minWidth: actions.minWidth || 180, textAlign: "center" }}
                    >
                      Actions
                    </th>
                  )}
                </tr>
              </thead>

              <tbody className="dt-tbody">
                {loading ? (
                  Array.from({ length: skeletonRowCount }).map((_, i) => (
                    <SkeletonRow
                      key={i}
                      colCount={columns.length}
                      hasBulkActions={hasBulkActions}
                      hasActions={hasActions}
                    />
                  ))
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={totalColSpan} className="dt-empty-cell">
                      <div className="dt-empty-state">
                        <div className="dt-empty-icon">
                          <InboxIcon sx={{ fontSize: 52, color: "#d1d5db" }} />
                        </div>
                        <p className="dt-empty-title">No Records Found</p>
                        <p className="dt-empty-sub">Try adjusting your search or filter criteria</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  rows.map((row, rowIndex) => {
                    const rowId = getRowId ? getRowId(row) : row.id;
                    const isSelected = selectedRows.includes(rowId);
                    return (
                      <tr
                        key={rowId ?? rowIndex}
                        className={`dt-row${isSelected ? " dt-row-selected" : ""}`}
                      >
                        {hasBulkActions && (
                          <td className="dt-cell dt-cell-checkbox">
                            <Checkbox
                              size="small"
                              onChange={(event) => handleRowSelect(event, row)}
                              checked={isSelected}
                              sx={MyCheckboxIcon}
                            />
                          </td>
                        )}
                        {columns.map((col) => (
                          <td
                            key={col.field}
                            className="dt-cell"
                            style={{
                              minWidth: col.minWidth ? `${col.minWidth}px` : undefined,
                              textAlign: col.align || "left"
                            }}
                          >
                            {renderCellContent(col, row)}
                          </td>
                        ))}
                        {hasActions && (
                          <td className="dt-cell dt-cell-actions">
                            {renderActionCell(row)}
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Footer ── */}
      {!toolbarOnly && showPagination && (
        <div className="dt-footer">

          {showRowsPerPage && (
            <div className="dt-footer-left">
              <span className="dt-rpp-label">Rows per page</span>

              <FormControl variant="standard" style={{ minWidth: 50 }}>
                <Select
                  value={pageSize}
                  onChange={(e) =>
                    onPageSizeChange(Number(e.target.value))
                  }
                >
                  {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          )}

          <Pagination
            count={Math.ceil(totalCount / pageSize)}
            page={page}
            siblingCount={0}
            variant="outlined"
            shape="rounded"
            className="custom-pagination"
            onChange={(event, value) => onPageChange(value)}
          />
        </div>
      )}


      {/* Filter Sidebar Popover */}
      <FilterDrawer
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        filters={drawerFilters}
        filterValues={filterValues}
        onApply={onFilterApply}
        onReset={onFilterReset}
      />
    </div>
  );
};

export default DataTable;
